import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { PrivacyProvider } from "./context/PrivacyContext";
import { Layout } from "./components/layout/Layout";
import AIChat from "./components/AIChat";

// Pages
import Login from "./pages/Login";
import InitialSetup from "./pages/InitialSetup";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import Installations from "./pages/Installations";
import Maintenance from "./pages/Maintenance";
import Cleaning from "./pages/Cleaning";
import Opportunities from "./pages/Opportunities";
import CSVImport from "./pages/CSVImport";
import Agenda from "./pages/Agenda";
import Settings from "./pages/Settings";
import Export from "./pages/Export";
import AdminPanel from "./pages/AdminPanel";
import InstallationMap from "./pages/InstallationMap";
import Aanvragen from "./pages/Aanvragen";
import Offertes from "./pages/Offertes";
import Facturen from "./pages/Facturen";
import Leads from "./pages/Leads";
import Financieel from "./pages/Financieel";
import Sharepoint from "./pages/Sharepoint";
import SharePublic from "./pages/SharePublic";
import ReinigingAanvraag from "./pages/ReinigingAanvraag";
import TimeSheets from "./pages/TimeSheets";
import TimeSheetsTeam from "./pages/TimeSheetsTeam";
import PlatformAdmin from "./pages/PlatformAdmin";
import Producten from "./pages/Producten";
import Installaties from "./pages/Installaties";
import Stock from "./pages/Stock";
import Mails from "./pages/Mails";
import Tickets from "./pages/Tickets";
import AIPhone from "./pages/AIPhone";
import Keuringen from "./pages/Keuringen";
import Interventies from "./pages/Interventies";
import Sales from "./pages/Sales";
import Schemas from "./pages/Schemas";
import SchemaEditor from "./pages/SchemaEditor";

// Onthoud laatste route zodat de CRM daar heropent
const LAST_ROUTE_KEY = 'lastRoute';

const getLastRoute = () => {
  const r = localStorage.getItem(LAST_ROUTE_KEY);
  return r && r.startsWith('/') && !['/login', '/setup', '/'].includes(r) ? r : '/dashboard';
};

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    if (path && !['/login', '/setup', '/'].includes(path) && !path.startsWith('/share/')) {
      localStorage.setItem(LAST_ROUTE_KEY, path);
    }
  }, [location.pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, loading, hasRole, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !hasRole(roles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={getLastRoute()} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/share/:token" element={<SharePublic />} />
      <Route path="/reiniging-aanvraag/:leadId" element={<ReinigingAanvraag />} />
      <Route path="/reiniging-aanvraag" element={<ReinigingAanvraag />} />
      <Route path="/setup" element={<InitialSetup />} />
      {/* Register route removed - only admins can create accounts via Admin Panel */}

      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to={getLastRoute()} replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="sharepoint" element={
          <ProtectedRoute roles={['admin', 'technician', 'sales']}>
            <Sharepoint />
          </ProtectedRoute>
        } />
        <Route path="customers" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Customers />
          </ProtectedRoute>
        } />
        <Route path="klanten" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Customers />
          </ProtectedRoute>
        } />
        <Route path="customers/:id" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <CustomerDetail />
          </ProtectedRoute>
        } />
        <Route path="klanten/:id" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <CustomerDetail />
          </ProtectedRoute>
        } />
        <Route path="properties" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Properties />
          </ProtectedRoute>
        } />
        <Route path="adressen" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Properties />
          </ProtectedRoute>
        } />
        <Route path="properties/:id" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <PropertyDetail />
          </ProtectedRoute>
        } />
        <Route path="adressen/:id" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <PropertyDetail />
          </ProtectedRoute>
        } />
        <Route path="installations" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Installations />
          </ProtectedRoute>
        } />
        <Route path="maintenance" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Maintenance />
          </ProtectedRoute>
        } />
        <Route path="onderhoud" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Maintenance />
          </ProtectedRoute>
        } />
        <Route path="cleaning" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Cleaning />
          </ProtectedRoute>
        } />
        <Route path="reiniging" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Cleaning />
          </ProtectedRoute>
        } />
        <Route path="opportunities" element={
          <ProtectedRoute roles={['admin', 'sales']}>
            <Opportunities />
          </ProtectedRoute>
        } />
        <Route path="kansen" element={
          <ProtectedRoute roles={['admin', 'sales']}>
            <Opportunities />
          </ProtectedRoute>
        } />
        <Route path="aanvragen" element={
          <ProtectedRoute roles={['admin', 'sales']}>
            <Aanvragen />
          </ProtectedRoute>
        } />
        <Route path="sales" element={
          <ProtectedRoute roles={['admin']}>
            <Sales />
          </ProtectedRoute>
        } />
        <Route path="offertes" element={
          <ProtectedRoute roles={['admin', 'sales']}>
            <Offertes />
          </ProtectedRoute>
        } />
        <Route path="facturen" element={
          <ProtectedRoute roles={['admin']}>
            <Facturen />
          </ProtectedRoute>
        } />
        <Route path="leads" element={
          <ProtectedRoute roles={['admin']}>
            <Leads />
          </ProtectedRoute>
        } />
        <Route path="financieel" element={
          <ProtectedRoute roles={['admin']}>
            <Financieel />
          </ProtectedRoute>
        } />
        <Route path="timesheets" element={
          <ProtectedRoute roles={['cleaner', 'technician', 'admin']}>
            <TimeSheets />
          </ProtectedRoute>
        } />
        <Route path="timesheets-team" element={
          <ProtectedRoute roles={['admin']}>
            <TimeSheetsTeam />
          </ProtectedRoute>
        } />
        <Route path="mails" element={
          <ProtectedRoute roles={['admin', 'sales']}>
            <Mails />
          </ProtectedRoute>
        } />
        <Route path="tickets" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Tickets />
          </ProtectedRoute>
        } />
        <Route path="ai-telefoon" element={
          <ProtectedRoute roles={['admin']}>
            <AIPhone />
          </ProtectedRoute>
        } />
        <Route path="keuringen" element={
          <ProtectedRoute roles={['admin']}>
            <Keuringen />
          </ProtectedRoute>
        } />
        <Route path="interventies" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Interventies />
          </ProtectedRoute>
        } />
        <Route path="producten" element={
          <ProtectedRoute roles={['admin']}>
            <Producten />
          </ProtectedRoute>
        } />
        <Route path="stock" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <Stock />
          </ProtectedRoute>
        } />
        <Route path="installaties" element={
          <ProtectedRoute roles={['admin', 'technician', 'cleaner']}>
            <Installaties />
          </ProtectedRoute>
        } />
        <Route path="import" element={
          <ProtectedRoute roles={['admin']}>
            <CSVImport />
          </ProtectedRoute>
        } />
        <Route path="agenda" element={<Agenda />} />
        <Route path="schemas" element={
          <ProtectedRoute roles={['admin']}><Schemas /></ProtectedRoute>
        } />
        <Route path="schemas/:id" element={
          <ProtectedRoute roles={['admin']}><SchemaEditor /></ProtectedRoute>
        } />
        <Route path="map" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <InstallationMap />
          </ProtectedRoute>
        } />
        <Route path="kaart" element={
          <ProtectedRoute roles={['admin', 'technician']}>
            <InstallationMap />
          </ProtectedRoute>
        } />
        <Route path="export" element={
          <ProtectedRoute roles={['admin']}>
            <Export />
          </ProtectedRoute>
        } />
        <Route path="admin" element={
          <ProtectedRoute roles={['admin']}>
            <AdminPanel />
          </ProtectedRoute>
        } />
        <Route path="settings" element={
          <ProtectedRoute roles={['admin', 'technician', 'sales', 'cleaner']}>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="instellingen" element={
          <ProtectedRoute roles={['admin', 'technician', 'sales', 'cleaner']}>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="platform" element={
          <ProtectedRoute roles={['admin']}>
            <PlatformAdmin />
          </ProtectedRoute>
        } />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <PrivacyProvider>
              <AppRoutes />
              <RouteTracker />
              <AIChat />
              <Toaster position="top-right" richColors />
            </PrivacyProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
